import type { Suit, Rarity } from './types';

// All lifecycle moments where a card-effect can fire. The dispatcher
// (effect_dispatcher.ts) routes each event to the right scan-set of cards.
export type EffectTrigger =
  | 'on_game_start'        // once at draft.onEnd, after decks/missions/dropzones seeded
  | 'on_draw'              // when a card is moved from deck to hand
  | 'on_play'              // when a card is played (staged) by its owner
  | 'on_table'             // placed on dropzone with no take (one-shot)
  | 'on_capture'           // this card captures other cards via resolveTake
  | 'on_captured'          // this card is itself captured by an opponent's card
  | 'in_discard'           // passive while sitting in `discard` (read by scoring)
  | 'on_snatch'            // this card empties the dropzone (snatch)
  | 'on_resource_gained'   // broadcast: any player gains a resource
  | 'on_round_end';        // round boundary (stage.onEnd) — drives stateful effects

// MVP set + v2 stateful effects.
export type EffectKind =
  | 'give_resources'
  | 'take_only_singleton'
  | 'double_resources_when_taken'      // defensive:    bonus al captor quando la carta viene rubata (on_captured)
  | 'double_resources_when_played'     // offensive:    bonus al giocatore SEMPRE quando la gioca, anche se non prende nulla (on_play)
  | 'double_resources_when_capturing'  // offensive++:  bonus al giocatore SOLO se la giocata cattura qualcosa (on_capture)
  | 'double_resources_when_acquired'   // bidirezionale: bonus a chi finisce con la carta (on_capture + on_captured)
  | 'steal_resources'                  // aggressive:   on_play, ruba N risorse di una suit all'avversario (clamped a 0)
  // --- v2 stateful: usano CardEffectInstance.state, tick su on_round_end ---
  | 'decaying_value'                   // on_round_end: -1 al proprio valore finché su dropzone (min 1)
  | 'time_bomb'                        // on_round_end: miccia--; a 0 detona, distrugge carte sulla dropzone
  | 'freeze'                           // on_table: blocca la carta highest/lowest sulla dropzone per N round
  | 'ripening_loot'                    // on_round_end banca +N; on_captured versa il bancato al captor
  | 'held_charge'                      // on_round_end (in mano) accumula round; on_play paga round*N
  // --- v3 (2026-09) effetti legati alla disposizione a blocchi ---
  | 'stash_payout'                     // on_round_end: se è sul tavolo quando arriva la mano 2, paga N al proprietario
  | 'sleeper'                          // on_draw: +N al proprio valore quando arriva con la mano 2
  | 'seize_priority'                   // on_table: priorità su questa dropzone il prossimo round (la cede sull'altra)
  | 'getaway';                         // on_round_end: se è su una dropzone, si sposta sull'altra

export interface EffectParams {
  // sparse, kind-specific, JSON-serializable scalars only
  suit?: Suit;
  amount?: number;
  factor?: number;
  // --- stateful ---
  turns?: number;                  // freeze: durata del blocco in round
  target?: 'highest' | 'lowest';   // freeze: quale carta della dropzone bloccare
  blast?: 'all' | 'enemy';         // time_bomb: distrugge tutte le altre carte, o solo le avversarie
  countDeck?: boolean;             // held_charge: conta anche i round passati nel mazzo (Syndicate High Rollers)
}

// One concrete, per-card effect baked at deck-build time and embedded onto
// CardData.effects[]. JSON-serializable so it survives boardgame.io state sync.
export interface CardEffectInstance {
  id: string;            // `${cardId}_e0`
  templateId: string;    // key into EFFECT_TEMPLATES / registry
  // A single trigger, or an array when one stateful effect must react to
  // several lifecycle moments (e.g. ripening_loot ticks on_round_end and
  // pays out on_captured) while keeping one shared `state`.
  trigger: EffectTrigger | EffectTrigger[];
  kind: EffectKind;
  params: EffectParams;
  text: string;          // pre-rendered, suit-substituted display text
  state?: Record<string, number | string | boolean>; // mutable per-match state (stateful effects)
}

// Carrier info passed into instantiation; minimal shape so we don't depend on
// full CardData (which might transitively import this file).
export interface EffectCarrier {
  id: string;
  suit: Suit;
  value: number;
}

interface EffectTemplate {
  id: string;
  trigger: EffectTrigger | EffectTrigger[];
  kind: EffectKind;
  paramize: (carrier: EffectCarrier) => EffectParams;
  text: (carrier: EffectCarrier, params: EffectParams) => string;
  // Baked verbatim into CardEffectInstance.state at instantiation. Stateful
  // effects declare their starting counters here (e.g. { fuse: 3 }).
  initialState?: Record<string, number | string | boolean>;
}

// MVP: 4 effects, one per active suit, each on a fixed (suit, value) carrier.
// Bindings live in CARD_EFFECT_BINDINGS (deck.ts consults it at generation time).
export const EFFECT_TEMPLATES: Record<string, EffectTemplate> = {
  // drug 3 — on_play: +1 drug resource
  e_drug_payout: {
    id: 'e_drug_payout',
    trigger: 'on_play',
    kind: 'give_resources',
    paramize: () => ({ suit: 'drug', amount: 1 }),
    text: () => 'Cut and uncut: +1 product on play.',
  },
  // police 1 — declarative: only takeable as exact match (excluded from
  // subset-sum). Bound to value 1 because che è il valore più "tirato" nei
  // subset-sum (1+2=3, 1+5=6, ...). Su un 6 sarebbe no-op.
  e_police_unsplit: {
    id: 'e_police_unsplit',
    trigger: 'on_table',
    kind: 'take_only_singleton',
    paramize: () => ({}),
    text: (carrier) => `Heat doesn't split: exact ${carrier.value} only.`,
  },
  // DEFENSIVE — on_captured. Captor prende 2*value della suit della carta.
  e_double_payout_defensive: {
    id: 'e_double_payout_defensive',
    trigger: 'on_captured',
    kind: 'double_resources_when_taken',
    paramize: () => ({ factor: 2 }),
    text: (carrier) =>
      `Trapped goods: thief gets ${carrier.value * 2} ${carrier.suit}.`,
  },
  // OFFENSIVE (any play) — on_play. Bonus al proprietario indipendente dalla
  // presa.
  e_double_payout_offensive: {
    id: 'e_double_payout_offensive',
    trigger: 'on_play',
    kind: 'double_resources_when_played',
    paramize: () => ({ factor: 2 }),
    text: (carrier) =>
      `Quick cash: +${carrier.value} ${carrier.suit} on play.`,
  },
  // OFFENSIVE-on-capture — on_capture. Bonus solo su presa effettiva.
  e_double_payout_on_capture: {
    id: 'e_double_payout_on_capture',
    trigger: 'on_capture',
    kind: 'double_resources_when_capturing',
    paramize: () => ({ factor: 2 }),
    text: (carrier) =>
      `Heist bonus: ${carrier.value * 2} ${carrier.suit} on a take.`,
  },
  // BIDIREZIONALE — espande in 2 effects (on_capture + on_captured).
  e_double_payout_bidirectional: {
    id: 'e_double_payout_bidirectional',
    trigger: 'on_capture',
    kind: 'double_resources_when_acquired',
    paramize: () => ({ factor: 2 }),
    text: (carrier) =>
      `Hot package: any owner gets ${carrier.value * 2} ${carrier.suit}.`,
  },
  // GENERIC self payout (give 1 of own suit on play). Variante "generica per
  // suit" del e_drug_payout (che resta drug-specific per il flavor "product").
  e_self_payout: {
    id: 'e_self_payout',
    trigger: 'on_play',
    kind: 'give_resources',
    paramize: (carrier) => ({ suit: carrier.suit, amount: 1 }),
    text: (carrier) => `Payout: +1 ${carrier.suit} on play.`,
  },
  // STEAL — on_play. Ruba 1 risorsa della suit della carta all'avversario.
  // Clampato a 0: se l'avversario non ha quella risorsa, non scatta.
  e_shakedown: {
    id: 'e_shakedown',
    trigger: 'on_play',
    kind: 'steal_resources',
    paramize: (carrier) => ({ suit: carrier.suit, amount: 1 }),
    text: (carrier) => `Shakedown: steal 1 ${carrier.suit}.`,
  },

  // ============ v2 — STATEFUL EFFECTS ============
  // Tutti usano CardEffectInstance.state, ticchettato da on_round_end
  // (dispatchato in Game.ts stage.onEnd). Lo state iniziale è bakeato a
  // deck-build da `initialState` e vive solo per la durata del match.

  // decaying_value — la carta perde 1 valore ogni round che passa su una
  // dropzone (min 1). Nessuno state: la verità è `card.value` stesso.
  e_perishable: {
    id: 'e_perishable',
    trigger: 'on_round_end',
    kind: 'decaying_value',
    paramize: () => ({}),
    text: () => 'Perishable: loses 1 value each round it sits on a dropzone.',
  },

  // time_bomb (variante 'all') — dopo 3 round su una dropzone esplode e
  // distrugge ogni ALTRA carta lì sopra. La bomba stessa sopravvive.
  e_carbomb: {
    id: 'e_carbomb',
    trigger: 'on_round_end',
    kind: 'time_bomb',
    paramize: () => ({ blast: 'all' }),
    text: () => 'Rigged engine: 3 rounds on a dropzone, then it blows — destroys every other card there.',
    initialState: { fuse: 3 },
  },

  // time_bomb (variante 'enemy') — esplode dopo 3 round ma distrugge solo le
  // carte dell'avversario del proprietario della bomba.
  e_plantedbomb: {
    id: 'e_plantedbomb',
    trigger: 'on_round_end',
    kind: 'time_bomb',
    paramize: () => ({ blast: 'enemy' }),
    text: () => "Planted charge: 3 rounds on a dropzone, then it blows — destroys the rival's cards there.",
    initialState: { fuse: 3 },
  },

  // freeze (highest) — quando viene calata sul tavolo, congela la carta di
  // valore più alto già presente sulla stessa dropzone: non catturabile per
  // 2 round.
  e_lockdown_high: {
    id: 'e_lockdown_high',
    trigger: 'on_table',
    kind: 'freeze',
    paramize: () => ({ target: 'highest', turns: 2 }),
    text: () => "Lockdown: freezes the dropzone's highest card for 2 rounds.",
  },

  // freeze (lowest) — variante che congela la carta di valore più basso.
  e_lockdown_low: {
    id: 'e_lockdown_low',
    trigger: 'on_table',
    kind: 'freeze',
    paramize: () => ({ target: 'lowest', turns: 2 }),
    text: () => "Stakeout: freezes the dropzone's lowest card for 2 rounds.",
  },

  // ripening_loot — banca +1 della propria suit ogni round su una dropzone;
  // chi cattura la carta incassa tutto il bancato (oltre al normale value).
  // Multi-trigger: tick su on_round_end, payout su on_captured, uno state.
  e_ripening: {
    id: 'e_ripening',
    trigger: ['on_round_end', 'on_captured'],
    kind: 'ripening_loot',
    paramize: (carrier) => ({ suit: carrier.suit, amount: 1 }),
    text: (carrier) => `Appreciating asset: banks +1 ${carrier.suit} each round — the captor collects the lot.`,
    initialState: { banked: 0 },
  },

  // held_charge — accumula 1 per ogni round trattenuta in mano; quando la
  // giochi paga round*amount della sua suit. Multi-trigger: tick on_round_end
  // (in mano), payout on_play.
  e_slowburn: {
    id: 'e_slowburn',
    trigger: ['on_round_end', 'on_play'],
    kind: 'held_charge',
    paramize: (carrier) => ({ suit: carrier.suit, amount: 1 }),
    text: (carrier) => `Slow burn: +1 ${carrier.suit} for every round held in hand before playing.`,
    initialState: { roundsHeld: 0 },
  },

  // ============ v3 — EFFETTI LEGATI AI BLOCCHI (2026-09) ============
  // Pensati per la disposizione tavolo / mano 1 / mano 2: dove metti la
  // carta nel deck editor decide quanto rende. Vedi docs/card_ideas.md.

  // Stash — carta da blocco TAVOLO: se è ancora su una dropzone quando
  // arriva la mano 2, il proprietario incassa +3 della sua suit.
  e_stash: {
    id: 'e_stash',
    trigger: 'on_round_end',
    kind: 'stash_payout',
    paramize: (carrier) => ({ suit: carrier.suit, amount: 3 }),
    text: (carrier) => `Stash: still on the table when Hand 2 arrives? +3 ${carrier.suit}.`,
  },

  // Sleeper — carta da MANO 2: quando arriva con la seconda mano guadagna +1
  // valore per il resto della partita.
  e_sleeper: {
    id: 'e_sleeper',
    trigger: 'on_draw',
    kind: 'sleeper',
    paramize: () => ({ amount: 1 }),
    text: () => 'Sleeper: +1 value when it arrives with Hand 2.',
  },

  // Inside Man (police) — calata sul tavolo, ti dà la priorità su quella
  // dropzone il round successivo. Le due dropzone hanno sempre priorità
  // opposte, quindi la cedi sull'altra.
  e_inside_man: {
    id: 'e_inside_man',
    trigger: 'on_table',
    kind: 'seize_priority',
    paramize: () => ({}),
    text: () => 'Inside man: priority on this dropzone next round (you give it up on the other).',
  },

  // Getaway (cars) — a fine round, se è su una dropzone, "scappa" sull'altra.
  e_getaway: {
    id: 'e_getaway',
    trigger: 'on_round_end',
    kind: 'getaway',
    paramize: () => ({}),
    text: () => 'Getaway: at the end of each round it drives to the other dropzone.',
  },
};

// Effect bindings, indexed by RARITY first. Effects are tiered:
//   - street  → basic (give_resources, take_only_singleton).
//   - made    → tactical (the double_resources_* family + steal).
//   - kingpin → stateful (decay, time_bomb, freeze, ripening, held_charge).
// `effectsForCard` reads CUMULATIVELY: a kingpin card inherits the made and
// street bindings of the same (suit, value), so higher rarity ⇒ at least as
// many effects (often strictly more). Net: rarity is BOTH a qualitative gate
// (which effects exist) AND an additive amplifier (more effects stack).
export const CARD_EFFECT_BINDINGS: Partial<Record<Rarity, Partial<Record<Suit, Partial<Record<number, string[]>>>>>> = {
  // --- street: the basics. The card "pays its number" and at most one small
  // resource effect. Backbone of any starter deck.
  street: {
    drug:           { 3: ['e_drug_payout'] },
    police:         { 1: ['e_police_unsplit'] },
    jewels:         { 1: ['e_police_unsplit'] },
    'casino chips': { 3: ['e_self_payout'] },
    weapons:        { 1: ['e_police_unsplit'] },
  },
  // --- made: tactical kit — doubles + steal. Cards that change the math of
  // a single capture.
  made: {
    drug:               { 6: ['e_double_payout_bidirectional'], 4: ['e_stash'] },
    police:             { 6: ['e_double_payout_defensive'], 3: ['e_inside_man'] },
    money:              { 4: ['e_double_payout_defensive'], 6: ['e_double_payout_offensive'], 2: ['e_sleeper'] },
    cars:               { 5: ['e_double_payout_on_capture'], 2: ['e_double_payout_bidirectional'], 3: ['e_getaway'] },
    jewels:             { 6: ['e_double_payout_defensive'], 4: ['e_stash'] },
    'casino chips':     { 5: ['e_double_payout_offensive'] },
    'criminal contacts':{ 2: ['e_shakedown'], 6: ['e_double_payout_bidirectional'], 4: ['e_sleeper'] },
    weapons:            { 4: ['e_double_payout_on_capture'] },
  },
  // --- kingpin: the stateful "wow" effects. Each suit gets ONE (police gets
  // two — high/low lock). Rare cards that decide rounds.
  kingpin: {
    drug:               { 5: ['e_perishable'] },
    police:             { 4: ['e_lockdown_high'], 2: ['e_lockdown_low'] },
    money:              { 3: ['e_ripening'] },
    cars:               { 6: ['e_carbomb'] },
    jewels:             { 3: ['e_ripening'] },
    'casino chips':     { 2: ['e_slowburn'] },
    'criminal contacts':{ 3: ['e_slowburn'] },
    weapons:            { 6: ['e_plantedbomb'] },
  },
};

// Instanzia uno o più CardEffectInstance da un template. Quasi tutti i template
// producono UN solo effect; il template bidirezionale ne produce DUE (uno per
// trigger) con stessa kind, così il registry handler li gestisce in modo
// uniforme.
export function instantiateEffect(
  templateId: string,
  carrier: EffectCarrier,
  idSuffix: string
): CardEffectInstance[] {
  const tmpl = EFFECT_TEMPLATES[templateId];
  if (!tmpl) throw new Error(`Unknown effect template: ${templateId}`);
  const params = tmpl.paramize(carrier);
  const text = tmpl.text(carrier, params);
  const baseId = `${carrier.id}_e${idSuffix}`;

  // Caso speciale: la bidirezionale espande in due entry, una per trigger.
  // Usa on_capture (non on_play) per la parte "tua presa" così il bonus non
  // scatta se giochi e non catturi (semantica "chi possiede la carta").
  if (tmpl.kind === 'double_resources_when_acquired') {
    return [
      { id: `${baseId}a`, templateId: tmpl.id, trigger: 'on_capture',  kind: tmpl.kind, params, text },
      { id: `${baseId}b`, templateId: tmpl.id, trigger: 'on_captured', kind: tmpl.kind, params, text },
    ];
  }

  return [{
    id: baseId,
    templateId: tmpl.id,
    trigger: tmpl.trigger,
    kind: tmpl.kind,
    params,
    text,
    // Stateful effects bake their starting counters here. Copied (not shared)
    // so each card instance owns a fresh state object.
    ...(tmpl.initialState ? { state: { ...tmpl.initialState } } : {}),
  }];
}

// Helper for deck.ts and starter_pack.ts: returns the effects for a given
// (suit, value, rarity), CUMULATIVE across tiers — street first, then made
// (if rarity ≥ made), then kingpin (if rarity = kingpin). Stable order.
// Returns undefined if the card has no effect at any inherited tier.
export function effectsForCard(
  suit: Suit,
  value: number,
  rarity: Rarity,
  cardId: string,
): CardEffectInstance[] | undefined {
  const tiers: Rarity[] = rarity === 'kingpin'
    ? ['street', 'made', 'kingpin']
    : rarity === 'made'
    ? ['street', 'made']
    : ['street'];
  const ids: string[] = [];
  for (const tier of tiers) {
    const tierIds = CARD_EFFECT_BINDINGS[tier]?.[suit]?.[value];
    if (tierIds) ids.push(...tierIds);
  }
  if (ids.length === 0) return undefined;
  return ids.flatMap((tid, i) =>
    instantiateEffect(tid, { id: cardId, suit, value }, String(i))
  );
}
